import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { nanoid } from "nanoid";
import { trpc } from "@/lib/trpc";
import {
  calculateArrGapResult,
  contextQuestionsForTrack,
  questionsForTrack,
  scoreLabelFor,
  shuffledAnswers,
  type ArrGapAnswers,
  type ArrGapContext,
  type ArrGapQuestion,
  type ArrGapTrack,
} from "@/data/arrGapCheck";
import { getArrGapReportView, type ArrGapReportBlock } from "@/data/arrGapReport";

type Step = "intro" | "routing" | "capability" | "context" | "sanity" | "teaser" | "gate" | "thanks";
type AnswerOrder = Record<ArrGapQuestion["id"], ReturnType<typeof shuffledAnswers>>;
type RoutingQuestion = "hirer" | "hire";

type LeadDetails = {
  name: string;
  email: string;
  company: string;
  role: string;
  linkedIn: string;
  consent: boolean;
};

function scrollTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function formRoleForHirer(hirerRole?: string) {
  if (hirerRole === "founder-ceo") return "Founder or CEO";
  if (hirerRole === "product-leader") return "CPO or VP or Head of Product";
  if (hirerRole === "investor-talent-leader") return "Investor or operating partner";
  return "Other";
}

function progressFor(step: Step, capabilityIndex: number, contextIndex: number, questionCount: number, contextCount: number, routingQuestion: RoutingQuestion) {
  const total = questionCount + contextCount + 2;
  if (step === "intro") return 0;
  if (step === "routing") return Math.round(((routingQuestion === "hirer" ? 1 : 2) / total) * 100);
  if (step === "capability") return Math.round(((capabilityIndex + 3) / total) * 100);
  if (step === "context" || step === "sanity") return Math.round(((questionCount + contextIndex + 3) / total) * 100);
  return 100;
}

function questionPosition(step: Step, capabilityIndex: number, contextIndex: number, questionCount: number, routingQuestion: RoutingQuestion) {
  if (step === "routing") return routingQuestion === "hirer" ? 1 : 2;
  if (step === "capability") return capabilityIndex + 3;
  if (step === "context" || step === "sanity") return questionCount + contextIndex + 3;
  return 0;
}

function DimensionScoreBar({ score, title }: { score: 1 | 2 | 3 | 4; title: string }) {
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-4 font-ui text-sm font-semibold text-[#173d2d]">
        <span>{title}</span>
        <span className="text-[#587063]">{scoreLabelFor(score)}</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5" aria-label={`${title}: ${scoreLabelFor(score)}`}>
        {[1, 2, 3, 4].map((step) => (
          <span key={step} className={`h-2 rounded-full ${step <= score ? "bg-[#8f7441]" : "bg-[#dce5dd]"}`} />
        ))}
      </div>
    </div>
  );
}

function SharedReportBlocks({ blocks, onButtonClick }: { blocks: ArrGapReportBlock[]; onButtonClick?: (event: React.MouseEvent<HTMLAnchorElement>, block: ArrGapReportBlock) => void }) {
  return <div className="mt-9 grid gap-7">
    {blocks.map((block, index) => <section key={`${block.heading ?? "section"}-${index}`} className="border-t border-[#dfe6df] pt-7">
      {block.heading && <h2 className="font-ui text-xs font-semibold uppercase tracking-[0.14em] text-[#8f7441]">{block.heading}</h2>}
      <div className="mt-4 grid gap-3 font-body text-lg leading-7 text-[#456052]">
        {block.lines.map((line) => <p key={line}>{line}</p>)}
      </div>
      {block.button && <a href={block.button.href} onClick={(event) => onButtonClick?.(event, block)} className="mt-5 inline-flex min-h-12 items-center justify-center bg-[#173d2d] px-6 font-ui text-sm font-semibold text-white no-underline transition hover:bg-[#8f7441] focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2">{block.button.label}</a>}
      {block.secondary && (block.secondary.href ? <a href={block.secondary.href} className="mt-5 block font-ui text-sm font-semibold text-[#173d2d] underline underline-offset-2">{block.secondary.label}</a> : <p className="mt-5 font-ui text-sm font-semibold text-[#173d2d]">{block.secondary.label}</p>)}
    </section>)}
  </div>;
}

export default function ArrHiringGapCheckPage() {
  const [step, setStep] = useState<Step>("intro");
  const [routingQuestion, setRoutingQuestion] = useState<RoutingQuestion>("hirer");
  const [capabilityIndex, setCapabilityIndex] = useState(0);
  const [contextIndex, setContextIndex] = useState(0);
  const [sessionId, setSessionId] = useState("");
  const [parentSessionId, setParentSessionId] = useState<string | undefined>();
  const [completionType, setCompletionType] = useState<"primary" | "partner">("primary");
  const [answerOrder, setAnswerOrder] = useState<AnswerOrder | null>(null);
  const [answers, setAnswers] = useState<ArrGapAnswers>({});
  const [context, setContext] = useState<ArrGapContext>({});
  const [lead, setLead] = useState<LeadDetails>({ name: "", email: "", company: "", role: "", linkedIn: "", consent: false });
  const [formError, setFormError] = useState("");
  const [utm, setUtm] = useState({ source: "", medium: "", campaign: "" });
  const [referrer, setReferrer] = useState("");

  const capturePartial = trpc.arrGapCheck.capturePartial.useMutation();
  const complete = trpc.arrGapCheck.complete.useMutation();
  const markShareCreated = trpc.arrGapCheck.markShareCreated.useMutation();

  useEffect(() => {
    document.title = "The ARR Hiring Gap Check | Alder Search";
    const description = "Find out whether your next product hire can actually move ARR. Under 5 minutes.";
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    meta?.setAttribute("content", description);
    const params = new URLSearchParams(window.location.search);
    const compare = params.get("compare");
    if (compare && /^[A-Za-z0-9_-]{8,64}$/.test(compare)) {
      setParentSessionId(compare);
      setCompletionType("partner");
      const parentHirerRole = params.get("hirer_role");
      const parentHiringFor = params.get("hiring_for");
      const parentTrack = params.get("track");
      if ((parentHirerRole === "founder-ceo" || parentHirerRole === "product-leader" || parentHirerRole === "investor-talent-leader") && (parentHiringFor === "product-leader" || parentHiringFor === "senior-product-manager" || parentHiringFor === "not-sure-yet") && (parentTrack === "Leader" || parentTrack === "Senior PM")) {
        setContext({ hirerRole: parentHirerRole, hiringFor: parentHiringFor, questionTrack: parentTrack });
      }
    }
    setUtm({
      source: params.get("utm_source") ?? "",
      medium: params.get("utm_medium") ?? "",
      campaign: params.get("utm_campaign") ?? "",
    });
    setReferrer(document.referrer.slice(0, 1000));
    return () => {
      document.title = "Alder Search | Retained Product Executive Search for VC and PE";
    };
  }, []);

  useEffect(() => {
    if (context.hirerRole) setLead((current) => current.role ? current : { ...current, role: formRoleForHirer(context.hirerRole) });
  }, [context.hirerRole]);

  const track: ArrGapTrack = context.questionTrack === "Senior PM" ? "Senior PM" : "Leader";
  const activeQuestions = useMemo(() => questionsForTrack(track, context.hirerRole), [track, context.hirerRole]);
  const activeContextQuestions = useMemo(() => contextQuestionsForTrack(track, context.hirerRole), [track, context.hirerRole]);
  const result = useMemo(() => calculateArrGapResult(answers, context, track), [answers, context, track]);
  const reportView = useMemo(() => result ? getArrGapReportView({ sessionId, name: lead.name || "there", email: lead.email, context, answers }) : null, [answers, context, lead.email, lead.name, result, sessionId]);
  const currentQuestion = activeQuestions[capabilityIndex];
  const currentContextQuestion = activeContextQuestions[contextIndex];
  const progress = progressFor(step, capabilityIndex, contextIndex, activeQuestions.length, activeContextQuestions.length, routingQuestion);
  const totalQuestions = activeQuestions.length + activeContextQuestions.length + 2;
  const currentQuestionPosition = questionPosition(step, capabilityIndex, contextIndex, activeQuestions.length, routingQuestion);

  const persistPartial = (nextAnswers: ArrGapAnswers, nextContext: ArrGapContext, id = sessionId) => {
    if (!id) return;
    capturePartial.mutate({
      sessionId: id,
      parentSessionId,
      completionType,
      utm,
      referrer,
      context: nextContext,
      answers: nextAnswers,
    });
  };

  const start = () => {
    const id = nanoid(16);
    const existingTrack: ArrGapTrack = context.questionTrack === "Senior PM" ? "Senior PM" : "Leader";
    setSessionId(id);
    setRoutingQuestion(context.hirerRole ? "hire" : "hirer");
    if (context.hirerRole && context.hiringFor && context.questionTrack) {
      const questions = questionsForTrack(existingTrack, context.hirerRole);
      setAnswerOrder(Object.fromEntries(questions.map((question) => [question.id, shuffledAnswers(question.answers)])) as AnswerOrder);
      setStep("capability");
    } else {
      setStep("routing");
    }
    scrollTop();
  };

  const chooseRouting = (value: string) => {
    if (routingQuestion === "hirer") {
      setContext({ hirerRole: value });
      setAnswers({});
      setCapabilityIndex(0);
      setContextIndex(0);
      setLead((current) => ({ ...current, role: formRoleForHirer(value) }));
      setRoutingQuestion("hire");
      scrollTop();
      return;
    }
    const questionTrack: ArrGapTrack = value === "senior-product-manager" ? "Senior PM" : "Leader";
    const nextContext: ArrGapContext = { hirerRole: context.hirerRole, hiringFor: value, questionTrack };
    const questions = questionsForTrack(questionTrack, nextContext.hirerRole);
    setContext(nextContext);
    setAnswers({});
    setAnswerOrder(Object.fromEntries(questions.map((question) => [question.id, shuffledAnswers(question.answers)])) as AnswerOrder);
    setCapabilityIndex(0);
    setContextIndex(0);
    setStep("capability");
    persistPartial({}, nextContext);
    scrollTop();
  };

  const chooseAnswer = (score: 1 | 2 | 3 | 4) => {
    if (!currentQuestion) return;
    const nextAnswers = { ...answers, [currentQuestion.id]: score } as ArrGapAnswers;
    setAnswers(nextAnswers);
    persistPartial(nextAnswers, context);
    if (capabilityIndex < activeQuestions.length - 1) {
      setCapabilityIndex((index) => index + 1);
      scrollTop();
      return;
    }
    setStep("context");
    scrollTop();
  };

  const advanceContext = (nextContext: ArrGapContext) => {
    persistPartial(answers, nextContext);
    if (contextIndex < activeContextQuestions.length - 1) {
      setStep("context");
      setContextIndex((index) => index + 1);
    } else {
      setStep("teaser");
    }
    scrollTop();
  };

  const chooseContext = (value: string) => {
    if (!currentContextQuestion) return;
    const nextContext = { ...context, [currentContextQuestion.id]: value } as ArrGapContext;
    if (currentContextQuestion.id === "productTeamSize" && value !== "no-one") delete nextContext.unusualConfirmed;
    setContext(nextContext);
    if (currentContextQuestion.id === "productTeamSize" && value === "no-one" && context.headcount === "250-plus" && context.unusualConfirmed !== "yes") {
      setStep("sanity");
      scrollTop();
      return;
    }
    advanceContext(nextContext);
  };

  const confirmUnusualCombination = () => {
    const nextContext = { ...context, unusualConfirmed: "yes" } as ArrGapContext;
    setContext(nextContext);
    advanceContext(nextContext);
  };

  const changeUnusualCombination = () => {
    const { productTeamSize: _productTeamSize, unusualConfirmed: _unusualConfirmed, ...remaining } = context;
    setContext(remaining);
    setStep("context");
    scrollTop();
  };

  const goBack = () => {
    if (step === "routing") {
      if (routingQuestion === "hire") setRoutingQuestion("hirer");
      else setStep("intro");
    } else if (step === "capability") {
      if (capabilityIndex > 0) setCapabilityIndex((index) => index - 1);
      else {
        setStep("routing");
        setRoutingQuestion("hire");
      }
    } else if (step === "context") {
      if (contextIndex > 0) setContextIndex((index) => index - 1);
      else {
        setStep("capability");
        setCapabilityIndex(activeQuestions.length - 1);
      }
    } else if (step === "sanity") {
      setStep("context");
    } else if (step === "teaser") {
      setStep("context");
      setContextIndex(activeContextQuestions.length - 1);
    } else if (step === "gate") {
      setStep("teaser");
    }
    scrollTop();
  };

  const canGoBack = step !== "intro" && step !== "thanks" && !(step === "routing" && routingQuestion === "hirer");

  const submitGate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!result) return;
    setFormError("");
    if (!lead.consent) {
      setFormError("Tick the consent box to receive your report.");
      return;
    }
    try {
      await complete.mutateAsync({
        sessionId,
        parentSessionId,
        completionType,
        utm,
        referrer,
        context,
        answers,
        name: lead.name,
        email: lead.email,
        company: lead.company,
        role: lead.role,
        linkedIn: lead.linkedIn,
        marketingConsent: lead.consent,
        readiness: result.readiness,
        suggestedLevel: result.suggestedLevel,
        recommendationReason: result.recommendationReason,
        levelConfidence: result.levelRecommendation.confidence,
        levelReasonCode: result.levelRecommendation.reasonCode,
        levelWhyNot: result.levelRecommendation.whyNot ?? undefined,
        levelUnusual: result.levelRecommendation.unusual,
        levelNote: result.levelRecommendation.note ?? undefined,
        levelHireThisWhen: result.levelProfile.hireThisWhen,
        levelWhatTheyOwn: result.levelProfile.whatTheyOwn,
        levelMeasuredOn: result.levelProfile.measuredOn,
        levelRedFlag: result.levelProfile.redFlag,
        topRisk: result.firstFix?.title,
        secondRisk: result.risks[1]?.title,
        firstFix: result.firstFix?.title,
        watchItem: result.watchItem?.title,
        aiNotLever: result.aiNotLever,
        hotLead: result.hotLead,
      });
      setStep("thanks");
      scrollTop();
    } catch {
      setFormError("We couldn’t send your report just now. Please try again.");
    }
  };

  const levelRank: Record<string, number> = {
    "senior-pm-or-lead": 1,
    "senior-pm": 1,
    "lead-pm": 2,
    "principal-pm": 3,
    "head-of-product": 4,
    director: 5,
    vp: 6,
    "cpo-or-cpto": 7,
  };
  const suggestedLevelRank: Record<NonNullable<typeof result>["suggestedLevel"], number> = {
    "Senior PM": 1,
    "Lead PM": 2,
    "Principal PM": 3,
    "Head of Product": 4,
    "Director of Product": 5,
    "VP Product": 6,
    "CPO / CPTO": 7,
  };
  const leanMatches = result ? levelRank[context.lean ?? ""] === suggestedLevelRank[result.suggestedLevel] : false;
  const leanIsNoIdea = context.lean === "no-idea";
  const neutralLevelMessage = leanIsNoIdea || leanMatches
    ? "We have a recommended level for you."
    : "Our recommendation is different from the level you were leaning towards.";

  const handleReportAction = (event: React.MouseEvent<HTMLAnchorElement>, block: ArrGapReportBlock) => {
    if (block.heading !== "COMPARE YOUR ANSWERS" || !block.button || !sessionId) return;
    event.preventDefault();
    markShareCreated.mutate({ sessionId }, { onSettled: () => { window.location.assign(block.button!.href); } });
  };

  return (
    <div className="min-h-screen bg-white text-[#173d2d]">
      <header className="border-b border-[#dfe6df] bg-white">
        <div className="mx-auto flex min-h-20 max-w-5xl items-center justify-between px-5 sm:px-8">
          <Link href="/" className="font-display text-xl font-bold text-[#173d2d] no-underline">Alder Search</Link>
          <span className="font-ui text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-[#8f7441]">Product Leadership Research</span>
        </div>
      </header>

      {step !== "intro" && (
        <div className="border-b border-[#e8ece8] bg-[#f7f9f7]">
          <div className="mx-auto max-w-5xl px-5 py-4 sm:px-8">
            <div className="mb-2 flex items-center justify-between font-ui text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-[#587063]">
              <span>{currentQuestionPosition ? `Question ${currentQuestionPosition} of ${totalQuestions}` : "Your result"}</span>
              <span>{progress}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-[#dce5dd]" aria-label={`${progress}% complete`}>
              <div className="h-full rounded-full bg-[#8f7441] transition-[width] duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      )}

      <main className="mx-auto min-h-[calc(100vh-10rem)] max-w-5xl px-5 py-12 sm:px-8 sm:py-16">
        {canGoBack && <button onClick={goBack} className="mb-6 inline-flex min-h-11 items-center font-ui text-sm font-semibold text-[#173d2d] underline underline-offset-4 transition hover:text-[#8f7441] focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2">Back</button>}
        {step === "intro" && (
          <section className="max-w-3xl py-8 sm:py-16">
            <p className="font-ui text-xs font-semibold uppercase tracking-[0.16em] text-[#8f7441]">Alder Search</p>
            <h1 className="mt-5 font-display text-5xl leading-[1.02] tracking-[-0.035em] text-[#173d2d] sm:text-6xl md:text-7xl">The ARR Hiring Gap Check</h1>
            <p className="mt-7 max-w-2xl font-body text-2xl leading-9 text-[#456052]">Find out whether your next product hire can actually move ARR. Under 5 minutes.</p>
            <p className="mt-5 max-w-2xl font-body text-lg leading-7 text-[#456052]">For founders, CEOs and the product leaders who hire for them.</p>
            {completionType === "partner" && <p className="mt-7 border-l-2 border-[#8f7441] pl-4 font-body text-lg leading-7 text-[#456052]">You are completing this with someone else. Your comparison arrives when you both finish.</p>}
            <button onClick={start} className="mt-11 min-h-14 bg-[#173d2d] px-9 font-ui text-sm font-semibold text-white transition hover:bg-[#8f7441] focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2">Start</button>
          </section>
        )}

        {step === "routing" && (
          <section className="mx-auto max-w-3xl py-4 sm:py-10">
            <h1 className="font-display text-4xl leading-[1.1] tracking-[-0.02em] text-[#173d2d] sm:text-5xl">{context.hirerRole === "investor-talent-leader" ? "Two quick questions, so the rest fits the company’s situation." : "Two quick questions, so the rest fits your situation."}</h1>
            <h2 className="mt-8 font-body text-2xl leading-8 text-[#456052]">{routingQuestion === "hirer" ? "Who is hiring?" : context.hirerRole === "investor-talent-leader" ? "Who is the company hiring?" : "Who are you hiring?"}</h2>
            <div className="mt-10 grid gap-3">
              {(routingQuestion === "hirer" ? [
                { label: "A founder or CEO", value: "founder-ceo" },
                { label: "A product leader (CPO, VP or Head of Product)", value: "product-leader" },
                { label: "An investor or talent leader", value: "investor-talent-leader" },
              ] : [
                { label: "A product leader (Head of Product, Director, VP, CPO or CPTO)", value: "product-leader" },
                { label: "A senior product manager (Senior PM, Lead or Principal)", value: "senior-product-manager" },
                { label: "Not sure yet", value: "not-sure-yet" },
              ]).map((option) => (
                <button key={option.value} onClick={() => chooseRouting(option.value)} className="group flex min-h-16 items-center justify-between border border-[#d5dfd6] bg-white px-5 py-4 text-left font-ui text-base font-semibold text-[#173d2d] transition hover:border-[#8f7441] hover:bg-[#f7f9f7] focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2">
                  <span>{option.label}</span><span aria-hidden="true" className="ml-6 shrink-0 text-[#8f7441] transition group-hover:translate-x-1">→</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {step === "capability" && currentQuestion && (
          <section className="mx-auto max-w-3xl py-4 sm:py-10">
            <p className="font-ui text-xs font-semibold uppercase tracking-[0.16em] text-[#8f7441]">Part 1 of 2</p>
            <p className="mt-3 font-ui text-xs font-semibold uppercase tracking-[0.16em] text-[#587063]">{currentQuestion.label}</p>
            <h1 className="mt-5 font-display text-4xl leading-[1.1] tracking-[-0.02em] text-[#173d2d] sm:text-5xl">{currentQuestion.prompt}</h1>
            <div className="mt-10 grid gap-3">
              {(answerOrder?.[currentQuestion.id] ?? currentQuestion.answers).map((answer) => (
                <button key={answer.label} aria-pressed={answers[currentQuestion.id] === answer.score} onClick={() => chooseAnswer(answer.score)} className={`group flex min-h-20 items-center justify-between border px-5 py-5 text-left font-body text-lg leading-7 text-[#173d2d] transition hover:border-[#8f7441] hover:bg-[#f7f9f7] focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2 ${answers[currentQuestion.id] === answer.score ? "border-[#8f7441] bg-[#f7f9f7]" : "border-[#d5dfd6] bg-white"}`}>
                  <span>{answer.label}</span><span aria-hidden="true" className="ml-6 shrink-0 font-ui text-[#8f7441] transition group-hover:translate-x-1">→</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {step === "context" && currentContextQuestion && (
          <section className="mx-auto max-w-3xl py-4 sm:py-10">
            <div className="flex items-center justify-between gap-4 font-ui text-xs font-semibold uppercase tracking-[0.16em] text-[#8f7441]"><span>About your company</span><span>Part 2 of 2</span></div>
            <h1 className="mt-5 font-display text-4xl leading-[1.1] tracking-[-0.02em] text-[#173d2d] sm:text-5xl">{currentContextQuestion.label}</h1>
            <div className="mt-10 grid gap-3">
              {currentContextQuestion.options.map((option) => (
                <button key={option.value} aria-pressed={context[currentContextQuestion.id] === option.value} onClick={() => chooseContext(option.value)} className={`group flex min-h-16 items-center justify-between border px-5 py-4 text-left font-ui text-base font-semibold text-[#173d2d] transition hover:border-[#8f7441] hover:bg-[#f7f9f7] focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2 ${context[currentContextQuestion.id] === option.value ? "border-[#8f7441] bg-[#f7f9f7]" : "border-[#d5dfd6] bg-white"}`}>
                  <span>{option.label}</span><span aria-hidden="true" className="ml-6 shrink-0 text-[#8f7441] transition group-hover:translate-x-1">→</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {step === "sanity" && (
          <section className="mx-auto max-w-3xl py-4 sm:py-10">
            <div className="flex items-center justify-between gap-4 font-ui text-xs font-semibold uppercase tracking-[0.16em] text-[#8f7441]"><span>About your company</span><span>Part 2 of 2</span></div>
            <h1 className="mt-5 font-display text-4xl leading-[1.1] tracking-[-0.02em] text-[#173d2d] sm:text-5xl">Just checking: 250 or more people and no product team?</h1>
            <div className="mt-10 grid gap-3">
              <button onClick={confirmUnusualCombination} className="group flex min-h-16 items-center justify-between border border-[#d5dfd6] bg-white px-5 py-4 text-left font-ui text-base font-semibold text-[#173d2d] transition hover:border-[#8f7441] hover:bg-[#f7f9f7] focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2"><span>Yes, that is right</span><span aria-hidden="true" className="ml-6 shrink-0 text-[#8f7441] transition group-hover:translate-x-1">→</span></button>
              <button onClick={changeUnusualCombination} className="group flex min-h-16 items-center justify-between border border-[#d5dfd6] bg-white px-5 py-4 text-left font-ui text-base font-semibold text-[#173d2d] transition hover:border-[#8f7441] hover:bg-[#f7f9f7] focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2"><span>Let me change it</span><span aria-hidden="true" className="ml-6 shrink-0 text-[#8f7441] transition group-hover:translate-x-1">→</span></button>
            </div>
          </section>
        )}

        {step === "teaser" && result && (
          <section className="mx-auto max-w-3xl py-4 sm:py-10">
            <h1 className="font-display text-4xl leading-[1.12] tracking-[-0.02em] text-[#173d2d] sm:text-5xl">Readiness score: {result.readiness}/100.</h1>
            <p className="mt-5 font-body text-xl leading-8 text-[#456052]">{result.readinessBand}.</p>
            <p className="mt-4 font-body text-base leading-7 text-[#456052]">Each answer scores 1 to 4. Your score is your total, rescaled so 6 is 0 and 24 is 100.</p>
            <div className="mt-8 grid gap-5 border-t border-[#dfe6df] pt-7">
              {result.dimensions.map((dimension) => <DimensionScoreBar key={dimension.id} title={dimension.title} score={dimension.score} />)}
            </div>
            {result.firstFix ? <div className="mt-8 border-l-2 border-[#8f7441] pl-5"><p className="font-ui text-xs font-semibold uppercase tracking-[0.16em] text-[#8f7441]">First thing to fix: {result.firstFix.title}</p><p className="mt-3 font-body text-xl leading-8 text-[#456052]">{result.firstFix.riskCopy}</p></div> : <p className="mt-8 border-l-2 border-[#8f7441] pl-5 font-body text-xl leading-8 text-[#456052]">No major risk found.</p>}
            <p className="mt-8 font-body text-xl leading-8 text-[#173d2d]">{neutralLevelMessage}</p>
            <p className="mt-5 font-body text-lg leading-7 text-[#456052]">Your recommended level, what to budget, the two things most likely to stall this hire, and how to run the process.</p>
            <button onClick={() => { setStep("gate"); scrollTop(); }} className="mt-6 min-h-14 bg-[#173d2d] px-8 font-ui text-sm font-semibold text-white transition hover:bg-[#8f7441] focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2">Send me the full report</button>
          </section>
        )}

        {step === "gate" && result && (
          <section className="mx-auto max-w-3xl py-4 sm:py-10">
            <p className="font-ui text-xs font-semibold uppercase tracking-[0.16em] text-[#8f7441]">Your report</p>
            <h1 className="mt-5 font-display text-4xl leading-[1.1] tracking-[-0.02em] text-[#173d2d] sm:text-5xl">Send me the full report</h1>
            <p className="mt-5 font-body text-xl leading-8 text-[#456052]">We will email your report and the follow-ups you requested.</p>
            <form onSubmit={submitGate} className="mt-10 grid gap-5">
              <label className="grid gap-2 font-ui text-sm font-semibold text-[#173d2d]">Name<input required value={lead.name} onChange={(event) => setLead({ ...lead, name: event.target.value })} className="min-h-13 border border-[#cfd9d0] px-4 font-ui font-normal outline-none focus:border-[#8f7441] focus:ring-2 focus:ring-[#8f7441]/25" /></label>
              <label className="grid gap-2 font-ui text-sm font-semibold text-[#173d2d]">Work email<input required type="email" value={lead.email} onChange={(event) => setLead({ ...lead, email: event.target.value })} className="min-h-13 border border-[#cfd9d0] px-4 font-ui font-normal outline-none focus:border-[#8f7441] focus:ring-2 focus:ring-[#8f7441]/25" /></label>
              <label className="grid gap-2 font-ui text-sm font-semibold text-[#173d2d]">Company <span className="font-normal text-[#657a6d]">Optional</span><input value={lead.company} onChange={(event) => setLead({ ...lead, company: event.target.value })} className="min-h-13 border border-[#cfd9d0] px-4 font-ui font-normal outline-none focus:border-[#8f7441] focus:ring-2 focus:ring-[#8f7441]/25" /></label>
              <label className="grid gap-2 font-ui text-sm font-semibold text-[#173d2d]">Role<select required value={lead.role} onChange={(event) => setLead({ ...lead, role: event.target.value })} className="min-h-13 border border-[#cfd9d0] bg-white px-4 font-ui font-normal outline-none focus:border-[#8f7441] focus:ring-2 focus:ring-[#8f7441]/25"><option>Founder or CEO</option><option>CPO or VP or Head of Product</option><option>Product Director</option><option>Talent or HR</option><option>Investor or operating partner</option><option>Other</option></select></label>
              <label className="grid gap-2 font-ui text-sm font-semibold text-[#173d2d]">LinkedIn <span className="font-normal text-[#657a6d]">Optional</span><input type="url" value={lead.linkedIn} onChange={(event) => setLead({ ...lead, linkedIn: event.target.value })} className="min-h-13 border border-[#cfd9d0] px-4 font-ui font-normal outline-none focus:border-[#8f7441] focus:ring-2 focus:ring-[#8f7441]/25" /></label>
              <label className="flex gap-3 font-ui text-sm leading-6 text-[#456052]"><input required type="checkbox" checked={lead.consent} onChange={(event) => setLead({ ...lead, consent: event.target.checked })} className="mt-1 h-4 w-4 accent-[#173d2d]" />I agree to receive my report and relevant product hiring insights from Alder Search. I can unsubscribe at any time.</label>
              <p className="font-ui text-xs leading-5 text-[#657a6d]">By submitting, you agree to the <Link href="/privacy" className="underline underline-offset-2">privacy notice</Link>.</p>
              {formError && <p role="alert" className="font-ui text-sm text-red-700">{formError}</p>}
              <button disabled={complete.isPending} className="min-h-14 bg-[#173d2d] px-8 font-ui text-sm font-semibold text-white transition hover:bg-[#8f7441] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#8f7441] focus:ring-offset-2">{complete.isPending ? "Sending..." : "Send me the full report"}</button>
            </form>
          </section>
        )}

        {step === "thanks" && result && reportView && (
          <section className="mx-auto max-w-3xl py-4 sm:py-10">
            <p className="font-body text-lg leading-7 text-[#456052]">Your report is on its way to {lead.email}. If it does not arrive in two minutes, check spam and add ryan@aldersearch.com to your contacts.</p>
            <SharedReportBlocks blocks={reportView.blocks} onButtonClick={handleReportAction} />
          </section>
        )}
      </main>

      <footer className="border-t border-[#dfe6df] bg-[#f7f9f7] py-8"><div className="mx-auto flex max-w-5xl flex-col gap-2 px-5 font-ui text-xs text-[#657a6d] sm:flex-row sm:items-center sm:justify-between sm:px-8"><span>Alder Search | Product Leadership Research</span><Link href="/privacy" className="underline underline-offset-2">Privacy notice</Link></div></footer>
    </div>
  );
}
